import { ConnectOptions } from 'twilio-video';
import { isMobile, removeUndefineds } from '..';
import { getResolution } from '../../state/settings/renderDimensions';
import { useAppState } from '../../state';

// The codec choice is remembered in localStorage (not per tab), so visiting the app once with
// ?codec=h264 on a host's machine makes every later tab there use it; ?codec=default clears it.
// Without an override the default is VP8 with simulcast, Twilio's recommendation for group rooms
// (the SFU can adapt each viewer's quality to their network).
export function getCodecChoice(): string {
  try {
    const match = window.location.search.match(/[?&]codec=(vp8|h264|auto|default)/i);
    if (match) {
      const value = match[1].toLowerCase();
      if (value === 'default') window.localStorage.removeItem('vuiCodec');
      else window.localStorage.setItem('vuiCodec', value);
    }
    return window.localStorage.getItem('vuiCodec') || '';
  } catch (e) {
    return '';
  }
}

function preferredVideoCodecs(roomType?: string): ConnectOptions['preferredVideoCodecs'] {
  switch (getCodecChoice()) {
    case 'h264':
      return [{ codec: 'H264' }];
    case 'auto':
      return 'auto';
    default:
      return [{ codec: 'VP8', simulcast: roomType !== 'peer-to-peer' }];
  }
}

export default function useConnectionOptions() {
  const { roomType, settings } = useAppState();

  // See: https://media.twiliocdn.com/sdk/js/video/releases/2.0.0/docs/global.html#ConnectOptions
  // for available connection options.
  const connectionOptions: ConnectOptions = {
    // Bandwidth Profile, Dominant Speaker, and Network Quality
    // features are only available in Small Group or Group Rooms.
    // Please set "Room Type" to "Group" or "Small Group" in your
    // Twilio Console: https://www.twilio.com/console/video/configure
    bandwidthProfile: {
      video: {
        mode: settings.bandwidthProfileMode,
        dominantSpeakerPriority: settings.dominantSpeakerPriority,
        renderDimensions: {
          low: getResolution(settings.renderDimensionLow),
          standard: getResolution(settings.renderDimensionStandard),
          high: getResolution(settings.renderDimensionHigh),
        },
        maxTracks: Number(settings.maxTracks),
      },
    },
    dominantSpeaker: true,
    networkQuality: { local: 1, remote: 1 },

    // Comment this line if you are playing music.
    maxAudioBitrate: Number(settings.maxAudioBitrate),

    // VP8 simulcast enables the media server in a Small Group or Group Room
    // to adapt your encoded video quality for each RemoteParticipant based on
    // their individual bandwidth constraints. Simulcast should be disabled if
    // you are using Peer-to-Peer Rooms.
    // VP8 with simulcast by default; override with ?codec=h264|auto|default (see above).
    preferredVideoCodecs: preferredVideoCodecs(roomType),
  };

  // For mobile browsers, limit the maximum incoming video bitrate to 2.5 Mbps.
  if (isMobile && connectionOptions?.bandwidthProfile?.video) {
    connectionOptions!.bandwidthProfile!.video!.maxSubscriptionBitrate = 2500000;
  }

  // Here we remove any 'undefined' values. The twilio-video SDK will only use defaults
  // when no value is passed for an option. It will throw an error when 'undefined' is passed.
  return removeUndefineds(connectionOptions);
}
